"use client";

import type React from "react";

import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Check } from "lucide-react";
import type { PlanFeatures, PlansOutput } from "@/interfaces/plan";
import { SubscriptionsService } from "@/services/SubscriptionsService";
import {
  type SubscriptionsInput,
  SubscriptionsPeriod,
} from "@/interfaces/subscriptions";
import { TooltipProvider } from "@/components/ui/tooltip";
import type { OrganizationsOutput } from "@/interfaces";
import { CouponsService } from "@/services/CouponsService";
import { Tabs, TabsList, TabsTrigger } from "./ui/tabs";
import { numberToMoney } from "@/util/moneyConversion";
import { Input } from "@/components/ui/input";
import { PlansService } from "@/services/PlansService";

interface PlansDialogProps {
  organization: OrganizationsOutput;
  children: React.ReactNode;
  plans: PlansOutput[];
}

export default function PlansDialog({
  organization,
  children,
  plans,
}: PlansDialogProps) {
  const [selectedPlan, setSelectedPlan] = useState<string>("");
  const [couponCode, setCouponCode] = useState<string>("");
  const [isValidatingCoupon, setIsValidatingCoupon] = useState<boolean>(false);
  const [couponValidation, setCouponValidation] = useState<{
    isValid?: boolean;
    message?: string;
    discount?: number;
  } | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [customPlan, setCustomPlan] = useState<PlansOutput | null>(null);
  const [period, setPeriod] = useState<SubscriptionsPeriod>(
    SubscriptionsPeriod.MONTHLY
  );
  const hasSetInitialPlan = useRef(false);

  // Garantindo que organization e organization.plan existam
  const currentPlanId = organization?.plan?.planId ?? null;
  const [validCouponId, setValidCouponId] = useState<string | null>(null);

  const couponService = new CouponsService();
  const subscriptionsService = new SubscriptionsService();
  const plansService = new PlansService();

  // Verifica se é um novo plano (primeiro cadastro)
  const isNewPlanRegistration = currentPlanId === null;

  // Buscar plano personalizado se necessário
  useEffect(() => {
    const fetchCustomPlan = async () => {
      if (currentPlanId && !plans.some((p) => p.id === currentPlanId)) {
        try {
          const customPlanData = await plansService.findById(currentPlanId);
          setCustomPlan(customPlanData);
          if (!hasSetInitialPlan.current) {
            setSelectedPlan(customPlanData.id);
          }
        } catch (error) {
          console.error("Erro ao buscar plano personalizado:", error);
        }
      }
    };

    fetchCustomPlan();
  }, [currentPlanId, plans]);

  // Definir plano padrão selecionado
  useEffect(() => {
    if (!hasSetInitialPlan.current) {
      if (currentPlanId) {
        setSelectedPlan(currentPlanId);
      } else if (plans.length > 0) {
        const firstPaidPlan = plans.find((p) => p.price > 0);
        setSelectedPlan(firstPaidPlan?.id || plans[0].id);
      }
      hasSetInitialPlan.current = true;
    }
  }, [currentPlanId, plans]);

  const validateCoupon = async () => {
    if (!couponCode.trim()) {
      setCouponValidation({
        isValid: false,
        message: "Digite um cupom",
      });
      return;
    }

    setIsValidatingCoupon(true);
    setCouponValidation(null);

    try {
      const result = await couponService.findAll({
        query: couponCode.trim(),
      });

      const [coupon] = result.data;

      if (!coupon) {
        setCouponValidation({
          isValid: false,
          message: "Cupom não encontrado",
        });
        return;
      }

      const isValid =
        new Date(coupon.expiresAt) > new Date() &&
        coupon.usageCount < coupon.limit;

      setValidCouponId(isValid ? coupon.id : null);

      setCouponValidation({
        isValid,
        message: isValid
          ? `Desconto: ${numberToMoney(coupon.value || 0)}`
          : "Cupom inválido",
        discount: isValid ? coupon.value : 0,
      });
    } catch {
      setCouponValidation({
        isValid: false,
        message: "Erro ao validar",
      });
    } finally {
      setIsValidatingCoupon(false);
    }
  };

  const handleSelect = async (planId: string) => {
    if (!planId) return;

    setIsLoading(true);

    try {
      const input: SubscriptionsInput = {
        planId,
        organizationId: organization.id,
        allowAdditional: false,
        couponId: isNewPlanRegistration
          ? validCouponId || undefined
          : undefined,
        period: period,
      };

      let response;
      if (currentPlanId) {
        response = await subscriptionsService.changePlan(input);
      } else {
        response = await subscriptionsService.create(input);
      }

      window.open(response.initPoint);
    } catch (error) {
      console.error("Erro ao processar assinatura:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const renderFeature = (feature: PlanFeatures) => {
    // Formatar o valor para exibição
    const displayValue = feature.displayValue || feature.value;
    const formattedValue = displayValue === "-" ? "Ilimitado" : displayValue;

    return (
      <li key={feature.id} className="flex items-center gap-2 py-0.5">
        <Check className="h-4 w-4 text-[#EE7B11] flex-shrink-0" />
        <span className="text-sm">
          {feature.displayName}
          {feature.value !== "false" && feature.value !== "true" && (
            <>
              : <span className="font-medium">{formattedValue}</span>
            </>
          )}
          {feature.value === "false" && ": não"}
          {feature.value === "true" && ": sim"}
        </span>
      </li>
    );
  };

  // Combinar planos padrão com plano personalizado, se existir
  const allPlans = customPlan
    ? [customPlan, ...plans.filter((p) => p.id !== customPlan.id)]
    : plans;

  // Calcular preço com desconto se houver cupom válido
  const getDiscountedPrice = (price: number) => {
    if (couponValidation?.isValid && couponValidation.discount) {
      return price - couponValidation.discount;
    }
    return price;
  };

  const handleDialogOpenChange = (open: boolean) => {
    if (!open) {
      // Reset states when dialog closes
      hasSetInitialPlan.current = false;
      setCouponCode("");
      setCouponValidation(null);
      setIsValidatingCoupon(false);
    }
  };

  // Encontrar o plano selecionado
  const selectedPlanData = allPlans.find((p) => p.id === selectedPlan);

  return (
    <TooltipProvider>
      <Dialog onOpenChange={handleDialogOpenChange}>
        <DialogTrigger asChild>{children}</DialogTrigger>
        <DialogContent className="sm:max-w-[450px] max-h-[90vh] overflow-y-auto">
          <DialogHeader className="space-y-1 pb-2">
            <DialogTitle>Alterar seu plano</DialogTitle>
            <DialogDescription className="text-xs">
              Escolha um novo plano para sua organização. Alterar o plano
              afetará imediatamente os recursos disponíveis.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <Tabs
              value={period}
              onValueChange={(value) => setPeriod(value as SubscriptionsPeriod)}
              className="w-full"
            >
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger
                  value={SubscriptionsPeriod.MONTHLY}
                  className="data-[state=active]:bg-[#EE7B11] data-[state=active]:text-white"
                >
                  Mensal
                </TabsTrigger>
                <TabsTrigger
                  value={SubscriptionsPeriod.ANUALLY}
                  className="data-[state=active]:bg-[#EE7B11] data-[state=active]:text-white"
                >
                  Anual
                </TabsTrigger>
              </TabsList>
            </Tabs>

            <RadioGroup
              value={selectedPlan}
              onValueChange={setSelectedPlan}
              className="space-y-2"
            >
              {allPlans.map((plan) => {
                const isCurrentPlanOption = plan.id === currentPlanId;
                const originalPrice =
                  period === SubscriptionsPeriod.ANUALLY
                    ? plan.price * 12
                    : plan.price;
                const displayPrice = getDiscountedPrice(originalPrice);

                return (
                  <div
                    key={plan.id}
                    className={`relative flex items-center justify-between rounded-md border p-3 ${
                      isCurrentPlanOption
                        ? "border-[#EE7B11] bg-[#EE7B11]/5"
                        : plan.id === selectedPlan
                        ? "border-[#EE7B11]"
                        : "border-gray-200"
                    }`}
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value={plan.id} id={`plan-${plan.id}`} />
                      <div>
                        <label
                          htmlFor={`plan-${plan.id}`}
                          className="text-sm font-medium"
                        >
                          {plan.name}
                        </label>
                        <p className="text-xs text-gray-500">
                          {plan.price === 0
                            ? "R$ 0,00"
                            : `${numberToMoney(displayPrice)}`}{" "}
                          por membro/
                          {period === SubscriptionsPeriod.MONTHLY
                            ? "mês"
                            : "ano"}
                        </p>
                      </div>
                    </div>
                    {isCurrentPlanOption && (
                      <span className="text-xs font-medium text-[#EE7B11]">
                        Plano Atual
                      </span>
                    )}
                  </div>
                );
              })}
            </RadioGroup>

            {isNewPlanRegistration && (
              <div className="flex items-center gap-2">
                <Input
                  type="text"
                  value={couponCode}
                  onChange={(e) => setCouponCode(e.target.value)}
                  placeholder="Cupom de desconto"
                  className="flex-1 h-8 text-xs"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={validateCoupon}
                  disabled={isValidatingCoupon || !couponCode.trim()}
                  className="border-[#EE7B11] text-[#EE7B11] hover:bg-[#EE7B11]/10 h-8 text-xs"
                >
                  {isValidatingCoupon ? "..." : "Aplicar"}
                </Button>
              </div>
            )}

            {couponValidation && (
              <p
                className={`text-xs ${
                  couponValidation.isValid ? "text-green-600" : "text-red-600"
                }`}
              >
                {couponValidation.message}
              </p>
            )}

            <div className="space-y-1">
              <h3 className="text-sm font-medium">Recursos incluídos:</h3>
              {selectedPlanData && (
                <ul className="space-y-0.5">
                  {selectedPlanData.features?.map(renderFeature)}
                </ul>
              )}
            </div>

            <Button
              type="button"
              className="w-full bg-[#EE7B11] hover:bg-[#EE7B11]/90"
              onClick={() => handleSelect(selectedPlan)}
              disabled={
                !selectedPlan || selectedPlan === currentPlanId || isLoading
              }
            >
              {isLoading
                ? "Processando..."
                : selectedPlan === currentPlanId
                ? "Plano Atual"
                : "Alterar plano"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </TooltipProvider>
  );
}
